import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBold, faHeading, faImage, faLink, faListOl, faListUl, faParagraph, faQuoteLeft, faUnderline } from '@fortawesome/free-solid-svg-icons';
import { saveData } from 'services/api/saveData';

/*
 * KnowledgeBaseEditor.tsx
 * Intercom like editor for knowledge base articles: a white page where the
 * text is typed and a floating toolbar appears on selection. Only headings,
 * paragraphs, bold, underline, lists, links, quotes and images are allowed,
 * every other format is dropped on typing and pasting. Images are uploaded
 * to the backend and embedded by their public url.
 */

// The formats the editor accepts, everything else is stripped
const ALLOWED_FORMATS = ['header', 'blockquote', 'bold', 'underline', 'list', 'link', 'image'];

interface KnowledgeBaseEditorProps {
    value: string;
    onChange: (value: string) => void;
    articleId?: number | null;
    placeholder?: string;
    onUploadError?: () => void;
    uploadApiUrl?: string; // The upload endpoint of the images, the knowledge base endpoint by default
    uploadData?: Record<string, any>; // Extra data sent with every image upload, the article by default
}

const KnowledgeBaseEditor: React.FC<KnowledgeBaseEditorProps> = ({
    value, onChange, articleId, placeholder, onUploadError, uploadApiUrl = 'post_knowledge_base_image_as_superuser', uploadData,
}) => {
    const { t } = useTranslation();
    const quillRef = useRef<ReactQuill>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeFormats, setActiveFormats] = useState<Record<string, any>>({});

    // Returns the quill editor instance
    const getEditor = () => quillRef.current?.getEditor();

    // Reads a file as a base64 data url
    const readFileAsDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    // Uploads an image and embeds it at the cursor position
    const uploadAndInsertImage = useCallback(async (file: File) => {
        const editor = getEditor();
        if (!editor || !file.type.startsWith('image/')) return;

        try {
            // Upload the image to the backend
            const imageData = await readFileAsDataUrl(file);
            const response = await saveData({
                apiUrl: uploadApiUrl,
                method: 'post',
                data: { image_file: imageData, image_filename: file.name, ...(uploadData ?? { article: articleId ?? null }) },
            });

            // Embed the image on its own line at the cursor
            const url = response?.data?.url;
            if (!url) throw new Error('No image url returned');
            const range = editor.getSelection(true);
            const index = range ? range.index : editor.getLength();
            editor.insertEmbed(index, 'image', url, 'user');
            editor.insertText(index + 1, '\n', 'user');
            editor.setSelection(index + 2, 0, 'user');
        } catch (error) {
            console.error('Image upload failed', error);
            if (onUploadError) onUploadError();
        }
    }, [articleId, onUploadError, uploadApiUrl, uploadData]);

    // Opens the file selector for an image
    const openImageSelector = () => {
        fileInputRef.current?.click();
    };

    // Handles the chosen file of the file selector
    const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) await uploadAndInsertImage(file);
    };

    // Configure the floating toolbar and the clipboard, memoized so quill is not reinitialized
    const modules = useMemo(() => ({
        toolbar: {
            container: [[{ header: 1 }, { header: 2 }], ['bold', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['blockquote', 'link', 'image']],
            handlers: {
                image: () => openImageSelector(),
            },
        },
        clipboard: { matchVisual: false },
    }), []);

    // Track the formats at the cursor to highlight the toolbar buttons
    useEffect(() => {
        const editor = getEditor();
        if (!editor) return;

        // Update the active formats on every selection or text change
        const updateFormats = () => {
            const range = editor.getSelection();
            setActiveFormats(range ? editor.getFormat(range) : {});
        };
        editor.on('editor-change', updateFormats);
        return () => { editor.off('editor-change', updateFormats); };
    }, []);

    // Upload pasted or dropped images instead of embedding them as base64
    useEffect(() => {
        const editor = getEditor();
        if (!editor) return;
        const root = editor.root;

        // Intercept image files from the clipboard or a drop
        const handleFiles = (event: ClipboardEvent | DragEvent) => {
            const files = event instanceof DragEvent ? event.dataTransfer?.files : event.clipboardData?.files;
            const imageFile = files ? Array.from(files).find(file => file.type.startsWith('image/')) : undefined;
            if (imageFile) {
                event.preventDefault();
                uploadAndInsertImage(imageFile);
            }
        };
        root.addEventListener('paste', handleFiles as any);
        root.addEventListener('drop', handleFiles as any);
        return () => {
            root.removeEventListener('paste', handleFiles as any);
            root.removeEventListener('drop', handleFiles as any);
        };
    }, [uploadAndInsertImage]);

    // Toggles a block format on the current line
    const toggleBlockFormat = (format: 'header' | 'blockquote' | 'list', formatValue: number | boolean | string) => {
        const editor = getEditor();
        if (!editor) return;
        editor.focus();
        const current = editor.getFormat();
        const isActive = current[format] === formatValue;
        editor.format(format, isActive ? false : formatValue, 'user');
    };

    // Toggles an inline format like bold or underline on the selection
    const toggleInlineFormat = (format: 'bold' | 'underline') => {
        const editor = getEditor();
        if (!editor) return;
        editor.focus();
        const current = editor.getFormat();
        editor.format(format, !current[format], 'user');
    };

    // Resets the current line to a paragraph
    const setParagraph = () => {
        const editor = getEditor();
        if (!editor) return;
        editor.focus();
        editor.format('header', false, 'user');
        editor.format('blockquote', false, 'user');
        editor.format('list', false, 'user');
    };

    // Opens the link tooltip of the bubble theme for the current selection
    const insertLink = () => {
        const editor: any = getEditor();
        if (!editor) return;
        editor.focus();
        const range = editor.getSelection(true);
        if (!range || range.length === 0) return;
        if (editor.getFormat(range).link) {
            editor.format('link', false, 'user');
            return;
        }
        editor.theme?.tooltip?.edit('link', '');
    };

    // Determines the class of a toolbar button
    const buttonClass = (isActive: boolean) => `kb-editor-button ${isActive ? 'active' : ''}`;

    return (
        <div className='kb-editor'>
            <div className='kb-editor-toolbar'>
                <button type='button' className={buttonClass(activeFormats.header === 1)} onClick={() => toggleBlockFormat('header', 1)} title={t('knowledge_base.editor.heading_1')}>
                    <FontAwesomeIcon icon={faHeading} /><span className='kb-editor-button-sub'>1</span>
                </button>
                <button type='button' className={buttonClass(activeFormats.header === 2)} onClick={() => toggleBlockFormat('header', 2)} title={t('knowledge_base.editor.heading_2')}>
                    <FontAwesomeIcon icon={faHeading} /><span className='kb-editor-button-sub'>2</span>
                </button>
                <button type='button' className={buttonClass(!activeFormats.header && !activeFormats.blockquote && !activeFormats.list)} onClick={setParagraph} title={t('knowledge_base.editor.paragraph')}>
                    <FontAwesomeIcon icon={faParagraph} />
                </button>
                <button type='button' className={buttonClass(!!activeFormats.blockquote)} onClick={() => toggleBlockFormat('blockquote', true)} title={t('knowledge_base.editor.quote')}>
                    <FontAwesomeIcon icon={faQuoteLeft} />
                </button>
                <span className='kb-editor-toolbar-separator' />
                <button type='button' className={buttonClass(!!activeFormats.bold)} onClick={() => toggleInlineFormat('bold')} title={t('knowledge_base.editor.bold')}>
                    <FontAwesomeIcon icon={faBold} />
                </button>
                <button type='button' className={buttonClass(!!activeFormats.underline)} onClick={() => toggleInlineFormat('underline')} title={t('knowledge_base.editor.underline')}>
                    <FontAwesomeIcon icon={faUnderline} />
                </button>
                <span className='kb-editor-toolbar-separator' />
                <button type='button' className={buttonClass(activeFormats.list === 'ordered')} onClick={() => toggleBlockFormat('list', 'ordered')} title={t('knowledge_base.editor.ordered_list')}>
                    <FontAwesomeIcon icon={faListOl} />
                </button>
                <button type='button' className={buttonClass(activeFormats.list === 'bullet')} onClick={() => toggleBlockFormat('list', 'bullet')} title={t('knowledge_base.editor.bullet_list')}>
                    <FontAwesomeIcon icon={faListUl} />
                </button>
                <span className='kb-editor-toolbar-separator' />
                <button type='button' className={buttonClass(!!activeFormats.link)} onClick={insertLink} title={t('knowledge_base.editor.link')}>
                    <FontAwesomeIcon icon={faLink} />
                </button>
                <button type='button' className={buttonClass(false)} onClick={openImageSelector} title={t('knowledge_base.editor.image')}>
                    <FontAwesomeIcon icon={faImage} />
                </button>
                <input ref={fileInputRef} type='file' accept='image/*' className='kb-editor-file-input' onChange={handleFileSelected} />
            </div>
            <ReactQuill
                ref={quillRef}
                theme='bubble'
                value={value}
                onChange={onChange}
                modules={modules}
                formats={ALLOWED_FORMATS}
                placeholder={placeholder ? t(placeholder) : undefined}
            />
        </div>
    );
};

export default KnowledgeBaseEditor;
